import type { DiaClient } from './client';
import { cacheKey, getDb } from './dexie';
import type {
  Checklist,
  ChecklistItem,
  House,
  Request,
  Room,
  StaffDirectoryRow,
  StayPublic,
  Uuid,
} from './types';

/**
 * Read helpers.
 *
 * Every read goes through cacheRows, which writes what came back into
 * IndexedDB and, when the network is not there, answers from it. That is the
 * "cache every entity the user has opened" rule from Section 11, in one place
 * rather than in forty screens.
 */

export async function cacheRows<T extends { id: Uuid }>(entity: string, rows: T[]): Promise<T[]> {
  const db = getDb();
  const at = Date.now();
  await db.cache.bulkPut(
    rows.map((row) => ({ key: cacheKey(entity, row.id), entity, id: row.id, row, cachedAt: at })),
  );
  return rows;
}

export async function cachedRows<T>(entity: string): Promise<T[]> {
  const rows = await getDb().cache.where('entity').equals(entity).toArray();
  return rows.map((r) => r.row as T);
}

type Loaded = { data: unknown; error: { message: string } | null };

/**
 * True when the phone itself says there is no network at all.
 *
 * Worth asking before every read (defect D6). A fetch with no network does not
 * fail fast: the browser spends about seven seconds on it, and a screen that
 * reloads after each tick spends those seven seconds frozen. navigator.onLine
 * can be wrong the optimistic way (island Wi-Fi with the satellite down still
 * reads online) and never the other way, so a false is safe to believe.
 */
export function browserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Network first, cache second. Never throws for being offline. */
async function readThrough<T extends { id: Uuid }>(
  entity: string,
  load: () => PromiseLike<Loaded>,
): Promise<T[]> {
  if (browserOffline()) return cachedRows<T>(entity);
  try {
    const { data, error } = await load();
    if (error) throw new Error(error.message);
    if (Array.isArray(data)) return cacheRows(entity, data as T[]);
  } catch {
    // Expected on the island. Fall through to whatever we already have.
  }
  return cachedRows<T>(entity);
}

export function listHouses(client: DiaClient): Promise<House[]> {
  return readThrough<House>('houses', () =>
    client.from('houses').select('*').order('sort'),
  );
}

export function listRooms(client: DiaClient): Promise<Room[]> {
  return readThrough<Room>('rooms', () => client.from('rooms').select('*').order('sort'));
}

export function listChecklists(client: DiaClient): Promise<Checklist[]> {
  return readThrough<Checklist>('checklists', () =>
    client.from('checklists').select('*').eq('active', true),
  );
}

export function listChecklistItems(client: DiaClient, checklistId: Uuid): Promise<ChecklistItem[]> {
  return readThrough<ChecklistItem>(`checklist_items:${checklistId}`, () =>
    client
      .from('checklist_items')
      .select('*')
      .eq('checklist_id', checklistId)
      .eq('active', true)
      .order('sort'),
  );
}

export function listOpenRequests(client: DiaClient): Promise<Request[]> {
  return readThrough<Request>('requests', () =>
    client
      .from('requests')
      .select('*')
      .in('status', ['new', 'seen', 'in_progress'])
      .order('priority')
      .order('created_at', { ascending: false }),
  );
}

export function listStaysPublic(client: DiaClient): Promise<StayPublic[]> {
  return readThrough<StayPublic>('stays_public', () =>
    client.from('stays_public').select('*').order('arrives_on'),
  );
}

/** Colleagues, without a PIN hash anywhere near them. */
export function listStaffDirectory(client: DiaClient): Promise<StaffDirectoryRow[]> {
  return readThrough<StaffDirectoryRow>('staff_directory', () =>
    client.from('staff_directory').select('*').order('name'),
  );
}

/**
 * The list a crew member works from: every item of the checklist, expanded
 * across the rooms it applies to. Pure, so it is the same in a test and on a
 * phone with no signal.
 */
export function expandItemsForRooms(
  items: ChecklistItem[],
  rooms: Room[],
): Array<{ item: ChecklistItem; room: Room | null }> {
  const out: Array<{ item: ChecklistItem; room: Room | null }> = [];
  for (const item of items) {
    if (item.room_id) {
      const room = rooms.find((r) => r.id === item.room_id) ?? null;
      out.push({ item, room });
      continue;
    }
    if (item.applies_to_room_kind === 'house_open' || item.applies_to_room_kind === 'house_close') {
      out.push({ item, room: null });
      continue;
    }
    for (const room of rooms.filter((r) => r.room_kind === item.applies_to_room_kind)) {
      out.push({ item, room });
    }
  }
  return out;
}
